import { findBlock } from "./find";
import type { EmailDocument, Selection, TextBlockTarget } from "./types";

export function resolveTextEditingBlockAfterHistoryNavigation(
  document: EmailDocument,
  textEditingBlock: TextBlockTarget | null,
): TextBlockTarget | null {
  if (textEditingBlock === null) {
    return null;
  }

  const block = findBlock(
    document,
    textEditingBlock.rowId,
    textEditingBlock.columnId,
    textEditingBlock.blockId,
  );
  if (
    block?.type === "text" ||
    block?.type === "button" ||
    block?.type === "eyebrow"
  ) {
    return textEditingBlock.slotIndex === undefined ? textEditingBlock : null;
  }

  if (block?.type === "buttonRow") {
    const slotIndex = textEditingBlock.slotIndex;
    if (slotIndex === undefined || block.buttons[slotIndex] === undefined) {
      return null;
    }
    return textEditingBlock;
  }

  return null;
}

export function keepsTextEditing(
  selection: Selection,
  editing: TextBlockTarget | null,
): boolean {
  if (editing === null) {
    return false;
  }

  if (selection.kind === "block") {
    return (
      editing.slotIndex === undefined &&
      selection.rowId === editing.rowId &&
      selection.columnId === editing.columnId &&
      selection.blockId === editing.blockId
    );
  }

  if (selection.kind === "buttonRowSlot") {
    return (
      selection.rowId === editing.rowId &&
      selection.columnId === editing.columnId &&
      selection.blockId === editing.blockId &&
      selection.slotIndex === editing.slotIndex
    );
  }

  return false;
}

export function nextTextEditReentryGuard(
  selection: Selection,
  currentGuard: string | null,
): string | null {
  if (selection.kind === 'email') {
    return null;
  }
  if (
    selection.kind === 'block' &&
    currentGuard !== null &&
    currentGuard !== selection.blockId
  ) {
    return null;
  }
  if (
    selection.kind === 'buttonRowSlot' &&
    currentGuard !== null &&
    currentGuard !== selection.blockId
  ) {
    return null;
  }
  return currentGuard;
}

export function isTextToolbarTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('[data-text-toolbar], [data-text-toolbar-mount]') !== null
  );
}

export function isInlineMergeTagAutocompleteTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(".email-builder-merge-tag-autocomplete") !== null
  );
}

export function isEmailBuilderFieldMergeTagPopoverTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("[data-email-builder-field-merge-tag-popover]") !== null
  );
}

export function isTextFrameChromeTarget(
  hit: EventTarget | null,
  frame: Element | null | undefined,
): boolean {
  if (!(hit instanceof Element) || frame == null) {
    return false;
  }
  if (!frame.contains(hit) || isTextToolbarTarget(hit)) {
    return false;
  }
  return hit.closest('[data-editor-chrome]') !== null;
}

export function isTextBlockEditing(
  editing: TextBlockTarget | null,
  rowId: string,
  columnId: string,
  blockId: string,
  slotIndex?: number,
): boolean {
  if (editing === null) {
    return false;
  }

  if (
    editing.rowId !== rowId ||
    editing.columnId !== columnId ||
    editing.blockId !== blockId
  ) {
    return false;
  }

  if (slotIndex === undefined) {
    return editing.slotIndex === undefined;
  }

  return editing.slotIndex === slotIndex;
}
